import type { TFeatureFlags } from '@roko/ui/types/common'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { fetchFeatures } from '@/data/api/test/fetchFeatures'

const FeatureFlagContext = createContext<TFeatureFlags>({})
export const LOCAL_STORAGE_KEY = 'devFeatureFlags'

/**
 * Context provider for feature flags.
 *
 * This provider merges feature flags from three sources in the following priority:
 * 1. Developer overrides from localStorage (`devFeatureFlags`)
 * 2. Remote flags fetched via `fetchFeatures` (if `useRemote` is true and not in test env)
 * 3. Initial flags passed as props
 *
 * The merged flags are provided to the React context, allowing consumers to access feature flags via `useFeatureFlags`.
 *
 * @param children - React children to be rendered within the provider.
 * @param initialFlags - Optional initial feature flags to use as defaults.
 * @param useRemote - If true, fetches feature flags from a remote source using React Query.
 */

interface IFeatureProviderProps {
  children: ReactNode
  initialFlags?: TFeatureFlags
  useRemote?: boolean
}

export function FeatureFlagProvider({ children, initialFlags = {}, useRemote = false }: IFeatureProviderProps) {
  // Disable remote fetching automatically during test runs
  const isTest = import.meta.env.NODE_ENV === 'test'
  const effectiveUseRemote = !isTest && useRemote

  const { data: remoteFlags } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: fetchFeatures,
    enabled: effectiveUseRemote,
    staleTime: 60 * 1000 * 1000,
    retry: 1,
  })

  // Merge: initial > remote > devOverrides
  const mergedFlags = useMemo(() => {
    const devOverrides = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}')
    return {
      ...initialFlags,
      ...(remoteFlags ?? {}),
      ...devOverrides,
    }
  }, [initialFlags, remoteFlags])

  return <FeatureFlagContext.Provider value={mergedFlags}>{children}</FeatureFlagContext.Provider>
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext)
}
